import FormPanelLayer from '../FormPanelLayer/FormPanelLayer';
import { useFormPanelContext } from '../../../store/formPanel/FormPanelContext';

/**
 * @param {object} params
 * @param {boolean} params.isTop
 * @param {number} params.stackLength
 * @param {() => void} params.handleCloseModal
 * @param {() => void} params.closeTopFormPanel
 * @returns {(() => void) | undefined}
 */
function getLayerCloseHandler({
  isTop,
  stackLength,
  handleCloseModal,
  closeTopFormPanel,
}) {
  if (!isTop) return undefined;
  if (stackLength === 1) return handleCloseModal;
  return closeTopFormPanel;
}

/**
 * Renders all layers of the form side panel stack from FormPanelContext.
 * @param {object} props
 * @param {() => void} [props.onRootCloseAfterSuccess] - Called when the root form submits successfully
 * @returns {JSX.Element | null} Form panel stack
 */
function FormPanelStack({ onRootCloseAfterSuccess }) {
  const {
    stack,
    handleCloseModal,
    closeTopFormPanel,
    handleCloseAfterSuccess,
    completeCloseLayer,
  } = useFormPanelContext();

  if (!stack.length) return null;

  return (
    <>
      {stack.map((entry, index) => {
        const isTop = index === stack.length - 1;
        const isRoot = index === 0;
        const isPanelOpen = !entry.isClosing;

        const handleLayerClose = getLayerCloseHandler({
          isTop,
          stackLength: stack.length,
          handleCloseModal,
          closeTopFormPanel,
        });

        const handleLayerCloseAfterSuccess = isRoot && onRootCloseAfterSuccess
          ? (/** @type {ApiFormFieldsToUpdateItem[] | undefined} */ fieldsToUpdate) => {
            // Direct-edit openFormModal already POSTs set-attribut + invalidates on settle.
            // Closing only avoids a parallel table refetch that would still show stale data.
            if (fieldsToUpdate?.length && entry.onChildSuccess) {
              entry.onChildSuccess(fieldsToUpdate);
              handleCloseModal();
              return;
            }
            onRootCloseAfterSuccess();
          }
          : handleCloseAfterSuccess;

        return (
          <FormPanelLayer
            key={entry.id}
            formUrl={entry.formUrl}
            containerId={entry.id}
            stackDepth={index}
            isOpen={isPanelOpen}
            showBackdrop={isPanelOpen}
            onClose={handleLayerClose}
            onExitComplete={() => completeCloseLayer(entry.id)}
            onCloseAfterSuccess={handleLayerCloseAfterSuccess}
          />
        );
      })}
    </>
  );
}

export default FormPanelStack;
